<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import medicationRequestService from "@/services/medicationRequest";
import helperService from "@/services/helper";

export default {
  name: "PrescriptionHistory",
  components: {
    BaseSubNav,
    Spinner,
  },
  data() {
    return {
      isLoading: false,
      medicationRequests: [],
      searchInput: "",
    };
  },
  computed: {
    filteredMedicationRequests() {
      if (!this.searchInput) return this.medicationRequests;

      const query = this.searchInput.toLowerCase();
      return this.medicationRequests.filter((request) => {
        const patient = request.patient;
        const medication =
          request.medication?.name ||
          request.publicMedication?.name ||
          request.publicMedication?.brandName ||
          "";
        const diagnosis = this.getDiagnosisName(request);
        return (
          patient?.firstName?.toLowerCase().includes(query) ||
          patient?.lastName?.toLowerCase().includes(query) ||
          medication.toLowerCase().includes(query) ||
          diagnosis.toLowerCase().includes(query)
        );
      });
    },
  },
  watch: {
    "$route.name": {
      handler: function () {
        this.getData();
      },
      immediate: true,
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const res = await medicationRequestService.get({
          excludePending: true,
        });
        this.medicationRequests = res.docs || res || [];
      } catch (error) {
        console.error("Error loading medication requests:", error);
        this.$message.error("Error al cargar las solicitudes de medicamentos");
      } finally {
        this.isLoading = false;
      }
    },
    getFullName(patient) {
      return helperService.getFullName(patient);
    },
    getMedicationName(request) {
      if (request.medication?.name) {
        return request.medication.name;
      }
      if (request.publicMedication?.name) {
        return request.publicMedication.name;
      }
      if (request.publicMedication?.brandName) {
        return request.publicMedication.brandName;
      }
      return "Sin medicación";
    },
    getDiagnosisName(request) {
      if (typeof request.diagnosis === "string") {
        return request.diagnosis;
      }
      if (request.diagnosis?.name) {
        return request.diagnosis.name;
      }
      if (typeof request.publicDiagnosis === "string") {
        return request.publicDiagnosis;
      }
      if (request.publicDiagnosis?.name) {
        return request.publicDiagnosis.name;
      }
      return "Sin diagnóstico";
    },
    getStatusText(status) {
      const statusMap = {
        pending: "Pendiente",
        approved: "Aprobada",
        rejected: "Rechazada",
        cancelled: "Cancelada",
      };
      return statusMap[status] || status;
    },
    goToPatientDashboard(patient) {
      const patientId = typeof patient === "string" ? patient : patient._id;
      this.$router.push({
        name: "patient-dashboard",
        params: { id: patientId },
      });
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(
    navigationType="prescription"
    :showNavigation="true"
  )
    template(#title)
      | Historial de recetas

    template(#actions)
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        placeholder="Buscar por paciente, medicación o diagnóstico"
        :clearable="true"
      )

  .box.box--with-subnav
    .box__content--stretch
      spinner(v-if="isLoading && !medicationRequests.length")

      h2.no-results(v-if="!isLoading && !medicationRequests.length")
        | No hay solicitudes de medicamentos en el historial

      table(v-if="medicationRequests.length")
        thead
          tr
            th Paciente
            th Diagnóstico
            th Medicación
            th Estado
            th Fecha de creada
        tbody
          tr(
            v-for="request in filteredMedicationRequests"
            :key="request._id"
          )
            td(@click="goToPatientDashboard(request.patient)")
              | {{ getFullName(request.patient) }}
            td(@click="goToPatientDashboard(request.patient)")
              | {{ getDiagnosisName(request) }}
            td(@click="goToPatientDashboard(request.patient)")
              | {{ getMedicationName(request) }}
            td(@click="goToPatientDashboard(request.patient)")
              span.status-badge(:class="'status-' + request.status")
                | {{ getStatusText(request.status) }}
            td(@click="goToPatientDashboard(request.patient)")
              | {{ request.createdAt | formatDateAndTime }}
</template>

<style lang="scss" scoped>
.box--with-subnav {
  min-height: 100px;
}

.no-results {
  text-align: center;
  padding: 50px;
  font-size: 1.5em;
  width: 100%;
}

tbody td:hover {
  cursor: pointer;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;

  &.status-pending {
    background-color: #fdf6ec;
    color: #e6a23c;
  }

  &.status-approved {
    background-color: #f0f9ff;
    color: #67c23a;
  }

  &.status-rejected {
    background-color: #fef0f0;
    color: #f56c6c;
  }

  &.status-cancelled {
    background-color: #f4f4f5;
    color: #909399;
  }
}
</style>
